import React from 'react';
import { withTranslation } from 'react-i18next';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            errorCount: 0,
            showLogoutPrompt: false
        };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error('Error caught by boundary:', error, errorInfo);

        this.setState(prevState => {
            const newCount = prevState.errorCount + 1;

            if (newCount > 10) {
                return {
                    error,
                    errorInfo,
                    errorCount: newCount,
                    showLogoutPrompt: true
                };
            }

            return {
                error,
                errorInfo,
                errorCount: newCount
            };
        });
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
            showLogoutPrompt: false
        });
    };

    handleLogout = () => {
        localStorage.removeItem('jwt');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userType');
        sessionStorage.clear();
        window.location.href = '/';
    };

    handleDismissLogoutPrompt = () => {
        this.setState({ showLogoutPrompt: false });
    };

    render() {
        const { t } = this.props;

        if (this.state.hasError) {
            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    padding: '20px',
                    backgroundColor: '#f5f5f5'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '40px',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                        maxWidth: '500px',
                        width: '100%',
                        textAlign: 'center'
                    }}>
                        <h1 style={{ color: '#dc3545', marginBottom: '20px' }}>
                            {t('common:errorBoundary.title')}
                        </h1>
                        <p style={{ color: '#666', marginBottom: '30px' }}>
                            {t('common:errorBoundary.message')}
                        </p>

                        {this.state.showLogoutPrompt && (
                            <div style={{
                                backgroundColor: '#fff3cd',
                                border: '1px solid #ffc107',
                                borderRadius: '4px',
                                padding: '15px',
                                marginBottom: '20px',
                                textAlign: 'left'
                            }}>
                                <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#856404' }}>
                                    {t('common:errorBoundary.multipleErrors', { count: this.state.errorCount })}
                                </p>
                                <p style={{ margin: '0', fontSize: '14px', color: '#856404' }}>
                                    {t('common:errorBoundary.sessionRecommendation')}
                                </p>
                                <button
                                    onClick={this.handleLogout}
                                    style={{
                                        marginTop: '10px',
                                        padding: '8px 16px',
                                        backgroundColor: '#ffc107',
                                        color: '#000',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {t('common:errorBoundary.logoutAndLogin')}
                                </button>
                                <button
                                    onClick={this.handleDismissLogoutPrompt}
                                    style={{
                                        marginTop: '10px',
                                        marginLeft: '10px',
                                        padding: '8px 16px',
                                        backgroundColor: 'transparent',
                                        color: '#856404',
                                        border: '1px solid #ffc107',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '14px'
                                    }}
                                >
                                    {t('common:errorBoundary.skip')}
                                </button>
                            </div>
                        )}

                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details style={{
                                textAlign: 'left',
                                marginBottom: '20px',
                                padding: '10px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px'
                            }}>
                                <summary style={{ cursor: 'pointer', color: '#666' }}>
                                    {t('common:errorBoundary.devDetails')}
                                </summary>
                                <pre style={{
                                    marginTop: '10px',
                                    fontSize: '12px',
                                    overflow: 'auto'
                                }}>
                                    {this.state.error.toString()}
                                    {this.state.errorInfo && this.state.errorInfo.componentStack}
                                </pre>
                            </details>
                        )}

                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button
                                onClick={this.handleReset}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#007bff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '16px'
                                }}
                            >
                                {t('common:errorBoundary.tryAgain')}
                            </button>
                            <button
                                onClick={() => window.location.reload()}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '16px'
                                }}
                            >
                                {t('common:errorBoundary.refreshPage')}
                            </button>
                            <button
                                onClick={this.handleLogout}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '16px'
                                }}
                            >
                                {t('common:errorBoundary.logout')}
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default withTranslation()(ErrorBoundary);
